import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

import { WizardProfileTabContent } from "../../components/sidebar/WizardProfileTabContent";
import { WizardSidebarNavigation } from "../../components/wizardSidebarNavigation/WizardSidebarNavigation";
import { useDeleteProfileItem, useEditProfileItem } from "./components/hooks";
import { ActionsSection, MenuHeader, ProfileContent } from "./Profiles";
import { useIntlTranslate } from "../../hooks";
import { ProfileType } from "../../store/profiles/profiles.types";

import {
  isEndPressed,
  isHomePressed,
  isLeftArrowPressed,
  isRightArrowPressed,
} from "@sportal/cdk/helpers";

import "./Profiles.scss";

const getPrevTabId = (profiles, currentTab) => {
  const currentTabIndex = _.findIndex(profiles, { id: currentTab });

  return currentTabIndex === 0
    ? _.last(profiles).id
    : profiles[currentTabIndex - 1].id;
};

const getNextTabId = (profiles, currentTab) => {
  const currentTabIndex = _.findIndex(profiles, { id: currentTab });

  return currentTabIndex === profiles.length - 1
    ? _.head(profiles).id
    : profiles[currentTabIndex + 1].id;
};

const ProfilesTab = ({ profile }) => {
  const menuItems = [
    useEditProfileItem(profile),
    useDeleteProfileItem(profile),
  ];

  const isCustomProfile = profile.type === ProfileType.Custom;

  return (
    <WizardProfileTabContent
      type={profile.type}
      menu={isCustomProfile ? menuItems : null}
      profileId={profile.id}
      title={!isCustomProfile ? profile.type : profile.name}
    />
  );
};

const MultipleWizardProfileContent = ({ id }) => {
  return (
    <div
      aria-labelledby={`profileTab-${id}`}
      id={`profilePanel-${id}`}
      role="tabpanel"
      tabIndex="0"
    >
      <ProfileContent profileId={id} />
    </div>
  );
};

export const WizardProfiles = ({
  selected,
  onSelect,
  profiles,
  limit,
  customProfilesAmount,
}) => {
  const profileLimit = limit - 1; // 1 means hidden group for blocked devices
  const currentAmount = customProfilesAmount + 2; //TODO: 2 means default groups visitors and employees
  const translate = useIntlTranslate();

  if (profiles.length === 1) {
    const profileId = profiles[0].id;

    return (
      <div className="profiles">
        <h1
          className="profiles__title"
          aria-label={translate("aria_wizard_sidebar_header_title")}
        >
          <FormattedMessage id={"wizard_sidebar_header_title"} />
        </h1>
        <ProfileContent profileId={profileId} />
      </div>
    );
  }

  const handleProfileTabKeyPress = (e) => {
    let matchingKeyEvent = false;
    const setTab = (id) => {
      matchingKeyEvent = true;
      onSelect(id);
    };

    if (isHomePressed(e)) {
      setTab(_.head(profiles).id);
    } else if (isEndPressed(e)) {
      setTab(_.last(profiles).id);
    } else if (isLeftArrowPressed(e)) {
      setTab(getPrevTabId(profiles, selected));
    } else if (isRightArrowPressed(e)) {
      setTab(getNextTabId(profiles, selected));
    }

    if (matchingKeyEvent) {
      e.preventDefault();
    }
  };

  const prepareTabs = () =>
    _.map(profiles, (profile) => {
      const profileId = profile.id;

      return {
        id: profileId,
        header: <ProfilesTab profile={profile} />,
        content: <MultipleWizardProfileContent id={profileId} />,
        name: profile.name,
        type: profile.type,
      };
    });

  return (
    <div className="profiles">
      <WizardSidebarNavigation
        tabs={prepareTabs()}
        activeTab={selected}
        onTabClick={onSelect}
        onTabKeyPress={handleProfileTabKeyPress}
        sidebarMenuHeader={
          <MenuHeader
            profileLimit={profileLimit}
            profilesLength={profiles.length}
            currentAmount={currentAmount}
            isWizard={true}
          />
        }
        sideSection={
          <ActionsSection
            profileLimit={profileLimit}
            selectedProfileId={selected}
          />
        }
      />
    </div>
  );
};

WizardProfiles.propTypes = {
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  customProfilesAmount: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  selected: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};
